import { apiFetch, useRealtime } from '@portal/web-sdk';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { Fragment } from 'react';
import {
  CHAT_EVENTS,
  type Channel,
  type ChannelList,
  type ChatPeople,
  type Message,
  type MessagePage,
  type PinList,
  type Thread,
  type Unread,
} from '../contracts.js';

export const chatKeys = {
  all: ['chat'] as const,
  channels: ['chat', 'channels'] as const,
  browse: ['chat', 'browse'] as const,
  channel: (id: string) => ['chat', 'channel', id] as const,
  messages: (id: string) => ['chat', 'messages', id] as const,
  thread: (id: string) => ['chat', 'thread', id] as const,
  pins: (channelId: string) => ['chat', 'pins', channelId] as const,
  unread: ['chat', 'unread'] as const,
  people: ['chat', 'people'] as const,
};

export const useChannels = () =>
  useQuery({ queryKey: chatKeys.channels, queryFn: () => apiFetch<ChannelList>('chat/channels') });

export const useChannel = (id: string) =>
  useQuery({
    queryKey: chatKeys.channel(id),
    queryFn: () => apiFetch<Channel>(`chat/channels/${id}`),
    enabled: Boolean(id),
  });

export const useMessages = (id: string) =>
  useQuery({
    queryKey: chatKeys.messages(id),
    queryFn: () => apiFetch<MessagePage>(`chat/channels/${id}/messages?limit=50`),
    enabled: Boolean(id),
  });

export const useThread = (id: string) =>
  useQuery({
    queryKey: chatKeys.thread(id),
    queryFn: () => apiFetch<Thread>(`chat/messages/${id}/thread`),
    enabled: Boolean(id),
  });

export const usePins = (channelId: string) =>
  useQuery({
    queryKey: chatKeys.pins(channelId),
    queryFn: () => apiFetch<PinList>(`chat/channels/${channelId}/pins`),
  });

export const usePeople = () =>
  useQuery({
    queryKey: chatKeys.people,
    queryFn: () => apiFetch<ChatPeople>('chat/people'),
    staleTime: 5 * 60_000,
  });

/** Unread count for the menu badge, kept fresh by realtime events. */
export function useChatUnread(): number {
  const client = useQueryClient();
  const unread = useQuery({
    queryKey: chatKeys.unread,
    queryFn: () => apiFetch<Unread>('chat/unread'),
    staleTime: 60_000,
  });
  const refresh = () => void client.invalidateQueries({ queryKey: chatKeys.unread });
  useRealtime(CHAT_EVENTS.message, refresh);
  useRealtime(CHAT_EVENTS.read, refresh);
  useRealtime(CHAT_EVENTS.channel, refresh);
  useRealtime('connect', refresh);
  return unread.data?.unread ?? 0;
}

/** Adds or replaces a message in the cached page of its channel. */
export function mergeMessage(
  page: MessagePage | undefined,
  message: Message,
): MessagePage | undefined {
  if (!page) return page;
  const at = page.items.findIndex((m) => m.id === message.id);
  if (at >= 0) {
    const items = [...page.items];
    items[at] = message;
    return { ...page, items };
  }
  return { ...page, items: [...page.items, message] };
}

const URL_PATTERN = /(https?:\/\/[^\s<>"]+)/g;

/** Message text: line breaks kept, links clickable, @mentions highlighted. */
export function MessageBody({ body, names }: { body: string; names: string[] }) {
  const mention = names.length
    ? new RegExp(
        `(@(?:${names.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')}))`,
        'g',
      )
    : null;
  return (
    <p className="text-sm break-words whitespace-pre-wrap">
      {body.split(URL_PATTERN).map((part, i) =>
        i % 2 === 1 ? (
          <a
            key={i}
            href={part}
            target="_blank"
            rel="noreferrer noopener"
            className="text-primary underline underline-offset-2"
          >
            {part}
          </a>
        ) : mention ? (
          <Fragment key={i}>
            {part.split(mention).map((piece, j) =>
              j % 2 === 1 ? (
                <span key={j} className="rounded bg-primary/10 px-0.5 font-medium text-primary">
                  {piece}
                </span>
              ) : (
                piece
              ),
            )}
          </Fragment>
        ) : (
          part
        ),
      )}
    </p>
  );
}

/**
 * Puts a new or changed message where it shows: a reply in its open thread, a first-level message in
 * the channel's timeline (and as the root of its thread). Pinned lists refetch.
 */
export function applyMessage(client: QueryClient, m: Message): void {
  if (m.parentId) {
    client.setQueryData<Thread>(chatKeys.thread(m.parentId), (t) => {
      if (!t) return t;
      const at = t.items.findIndex((x) => x.id === m.id);
      const items = [...t.items];
      if (at >= 0) items[at] = m;
      else items.push(m);
      return { ...t, items };
    });
    return;
  }
  client.setQueryData<MessagePage>(chatKeys.messages(m.channelId), (p) => mergeMessage(p, m));
  client.setQueryData<Thread>(chatKeys.thread(m.id), (t) => (t ? { ...t, root: m } : t));
  void client.invalidateQueries({ queryKey: chatKeys.pins(m.channelId) });
}
